/**
 * StrategicPriorities - Game-Level Strategic Context
 *
 * Evaluates the overall game situation and determines the strategic priority
 * that should guide all unit decisions for the current turn.
 *
 * Priority Order (highest to lowest):
 * 1. SURVIVAL - Capital/HQ under direct threat
 * 2. CRITICAL_DEFENSE - Cities being captured
 * 3. COUNTER_ATTACK - Enemy overextended, opportunity to strike
 * 4. EXPANSION - Neutral cities available to capture
 * 5. OFFENSIVE - We have force advantage, push attack
 * 6. CONSOLIDATION - Units scattered/damaged, regroup
 * 7. PRODUCTION - Default, focus on building army
 */

import { FactionType, UnitCategory } from '../../../types';
import {
  GameStateSnapshot,
  UnitSnapshot,
  CitySnapshot,
  StrategicPriority,
  hexDistance,
  distanceToCity,
  isPositionOnCity
} from '../V3Types';
import { getUnitsForFaction, getCitiesForFaction } from '../core/GameStateSnapshot';

/**
 * Strategic analysis result
 */
export interface StrategicAnalysis {
  priority: StrategicPriority;
  threatenedCities: CitySnapshot[];
  captureTargets: CitySnapshot[];
  vulnerableEnemies: UnitSnapshot[];
  forceRatio: number;
  resourceAdvantage: number;
  recommendation: string;
}

/**
 * Analyze the strategic situation and determine priority
 */
export function analyzeStrategicPriority(
  snapshot: GameStateSnapshot,
  faction: FactionType,
  enemyFaction: FactionType
): StrategicAnalysis {
  const ourUnits = getUnitsForFaction(snapshot, faction);
  const enemyUnits = getUnitsForFaction(snapshot, enemyFaction);
  const ourCities = getCitiesForFaction(snapshot, faction);
  const enemyCities = getCitiesForFaction(snapshot, enemyFaction);
  const neutralCities = snapshot.cities.filter(c => c.faction === null);

  // Calculate force ratio
  const forceRatio = calculateForceRatio(ourUnits, enemyUnits);

  // Calculate resource advantage
  const ourResources = snapshot.resources.get(faction) || 0;
  const enemyResources = snapshot.resources.get(enemyFaction) || 0;
  const resourceAdvantage = ourResources - enemyResources;

  // Find threatened cities (being captured or enemies nearby)
  const threatenedCities = findThreatenedCities(ourCities, enemyUnits, enemyFaction);

  // Find capture targets (enemy and neutral cities we can reach)
  const captureTargets = findCaptureTargets(ourUnits, enemyCities, neutralCities);

  // Find vulnerable enemies
  const vulnerableEnemies = findVulnerableEnemies(enemyUnits, ourUnits);

  // Determine priority
  const { priority, recommendation } = determinePriority(
    ourUnits,
    enemyUnits,
    ourCities,
    threatenedCities,
    captureTargets,
    vulnerableEnemies,
    neutralCities,
    forceRatio
  );

  return {
    priority,
    threatenedCities,
    captureTargets,
    vulnerableEnemies,
    forceRatio,
    resourceAdvantage,
    recommendation
  };
}

/**
 * Calculate force ratio (our power / enemy power)
 */
function calculateForceRatio(ourUnits: UnitSnapshot[], enemyUnits: UnitSnapshot[]): number {
  const ourPower = calculateArmyPower(ourUnits);
  const enemyPower = calculateArmyPower(enemyUnits);

  if (enemyPower === 0) return 999; // We have total dominance
  return ourPower / enemyPower;
}

/**
 * Calculate total army power
 */
function calculateArmyPower(units: UnitSnapshot[]): number {
  return units.reduce((sum, unit) => {
    const healthFactor = unit.health / unit.maxHealth;
    const veteranBonus = unit.isVeteran ? 1.25 : 1.0;
    const basePower = unit.attack + unit.defense;
    return sum + basePower * healthFactor * veteranBonus;
  }, 0);
}

/**
 * Find cities that are threatened
 */
function findThreatenedCities(
  ourCities: CitySnapshot[],
  enemyUnits: UnitSnapshot[],
  enemyFaction: FactionType
): CitySnapshot[] {
  return ourCities.filter(city => {
    // Being actively captured by enemy
    if (city.isBeingCaptured && city.capturingFaction === enemyFaction) {
      return true;
    }

    // Enemy infantry within capture range (on city or adjacent)
    const enemyInfantryNearby = enemyUnits.filter(u =>
      u.category === UnitCategory.INFANTRY &&
      distanceToCity(u.position, city) <= 1
    );

    return enemyInfantryNearby.length > 0;
  });
}

/**
 * Find cities we can potentially capture
 */
function findCaptureTargets(
  ourUnits: UnitSnapshot[],
  enemyCities: CitySnapshot[],
  neutralCities: CitySnapshot[]
): CitySnapshot[] {
  const targets: CitySnapshot[] = [];
  const infantry = ourUnits.filter(u => u.category === UnitCategory.INFANTRY);

  // Check neutral cities first (easier targets)
  for (const city of neutralCities) {
    const closestInfantry = infantry.reduce((closest, unit) => {
      const dist = distanceToCity(unit.position, city);
      return dist < closest ? dist : closest;
    }, Infinity);

    // If any infantry can reach within ~3 turns, it's a target
    if (closestInfantry <= 10) {
      targets.push(city);
    }
  }

  // Check enemy cities
  for (const city of enemyCities) {
    const closestInfantry = infantry.reduce((closest, unit) => {
      const dist = distanceToCity(unit.position, city);
      return dist < closest ? dist : closest;
    }, Infinity);

    if (closestInfantry <= 8) {
      targets.push(city);
    }
  }

  return targets;
}

/**
 * Find vulnerable enemy units (low health or isolated)
 */
function findVulnerableEnemies(
  enemyUnits: UnitSnapshot[],
  ourUnits: UnitSnapshot[]
): UnitSnapshot[] {
  return enemyUnits.filter(enemy => {
    // Low health
    const isLowHealth = enemy.health < enemy.maxHealth * 0.4;

    // Isolated (no allies within 3 hexes)
    const nearbyAllies = enemyUnits.filter(ally =>
      ally.id !== enemy.id &&
      hexDistance(ally.position, enemy.position) <= 3
    );
    const isIsolated = nearbyAllies.length === 0;

    // We can attack it
    const canBeAttacked = ourUnits.some(unit =>
      hexDistance(unit.position, enemy.position) <= unit.range + unit.movement
    );

    return canBeAttacked && (isLowHealth || isIsolated);
  });
}

/**
 * Determine the strategic priority based on analysis
 */
function determinePriority(
  ourUnits: UnitSnapshot[],
  _enemyUnits: UnitSnapshot[],
  ourCities: CitySnapshot[],
  threatenedCities: CitySnapshot[],
  captureTargets: CitySnapshot[],
  vulnerableEnemies: UnitSnapshot[],
  _neutralCities: CitySnapshot[],
  forceRatio: number
): { priority: StrategicPriority; recommendation: string } {

  // 1. SURVIVAL - If we have few cities and they're threatened
  if (ourCities.length <= 2 && threatenedCities.length > 0) {
    return {
      priority: StrategicPriority.SURVIVAL,
      recommendation: 'Critical! Defend remaining cities at all costs.'
    };
  }

  // 2. CRITICAL_DEFENSE - Cities being captured
  const citiesBeingCaptured = threatenedCities.filter(c => c.isBeingCaptured);
  if (citiesBeingCaptured.length > 0) {
    return {
      priority: StrategicPriority.CRITICAL_DEFENSE,
      recommendation: `Liberate ${citiesBeingCaptured.length} city(s) being captured.`
    };
  }

  // 3. COUNTER_ATTACK - Vulnerable enemies when we're not weak
  if (vulnerableEnemies.length >= 2 && forceRatio > 0.8) {
    return {
      priority: StrategicPriority.COUNTER_ATTACK,
      recommendation: `Strike ${vulnerableEnemies.length} vulnerable enemy units.`
    };
  }

  // 4. EXPANSION - Neutral cities available
  const neutralTargets = captureTargets.filter(c => c.faction === null);
  if (neutralTargets.length > 0 && forceRatio > 0.6) {
    return {
      priority: StrategicPriority.EXPANSION,
      recommendation: `Capture ${neutralTargets.length} neutral city(s).`
    };
  }

  // 5. OFFENSIVE - Strong force advantage
  if (forceRatio > 1.5) {
    return {
      priority: StrategicPriority.OFFENSIVE,
      recommendation: 'Press the attack with force advantage.'
    };
  }

  // 6. CONSOLIDATION - Weak or scattered
  const damagedUnits = ourUnits.filter(u => u.health < u.maxHealth * 0.5);
  if (forceRatio < 0.7 || damagedUnits.length > ourUnits.length * 0.4) {
    return {
      priority: StrategicPriority.CONSOLIDATION,
      recommendation: 'Regroup and heal damaged units.'
    };
  }

  // 7. PRODUCTION - Default
  return {
    priority: StrategicPriority.PRODUCTION,
    recommendation: 'Build up forces and maintain positions.'
  };
}

/**
 * Get priority weight for move ordering
 * Higher weight = more important strategic objective
 */
export function getStrategicWeight(priority: StrategicPriority): number {
  switch (priority) {
    case StrategicPriority.SURVIVAL:
      return 1000;
    case StrategicPriority.CRITICAL_DEFENSE:
      return 800;
    case StrategicPriority.COUNTER_ATTACK:
      return 600;
    case StrategicPriority.EXPANSION:
      return 500;
    case StrategicPriority.OFFENSIVE:
      return 400;
    case StrategicPriority.CONSOLIDATION:
      return 300;
    case StrategicPriority.PRODUCTION:
    default:
      return 200;
  }
}

/**
 * Check if a move supports the current strategic priority
 */
export function moveSupportsStrategy(
  move: { to?: { x: number; y: number }; targetId?: string; flags: { capturesCity: boolean; threatensCity: boolean } },
  priority: StrategicPriority,
  analysis: StrategicAnalysis,
  unitPosition: { x: number; y: number }
): number {
  let bonus = 0;

  switch (priority) {
    case StrategicPriority.SURVIVAL:
    case StrategicPriority.CRITICAL_DEFENSE:
      // Bonus for moves toward threatened cities
      for (const city of analysis.threatenedCities) {
        if (move.to) {
          const currentDist = distanceToCity(unitPosition, city);
          const newDist = distanceToCity(move.to, city);
          if (newDist < currentDist) {
            bonus += (currentDist - newDist) * 50;
          }
          if (isPositionOnCity(move.to, city)) {
            bonus += 200; // Defending the city directly
          }
        }
      }
      break;

    case StrategicPriority.COUNTER_ATTACK:
      // Bonus for attacking vulnerable enemies
      if (move.targetId) {
        const isVulnerable = analysis.vulnerableEnemies.some(e => e.id === move.targetId);
        if (isVulnerable) {
          bonus += 150;
        }
      }
      break;

    case StrategicPriority.EXPANSION:
      // Bonus for moves toward neutral cities
      if (move.flags.capturesCity) {
        bonus += 300;
      }
      if (move.flags.threatensCity) {
        bonus += 100;
      }
      break;

    case StrategicPriority.OFFENSIVE:
      // Bonus for aggressive moves
      if (move.targetId) {
        bonus += 100;
      }
      if (move.flags.threatensCity) {
        bonus += 150;
      }
      break;

    case StrategicPriority.CONSOLIDATION:
      // Bonus for defensive positioning (handled in tactical priorities)
      break;

    case StrategicPriority.PRODUCTION:
      // No special bonus - let tactical priorities handle it
      break;
  }

  return bonus;
}
